import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Users, PaperPlaneTilt, Copy, Trash, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Praticien", assistant: "Assistant", research: "Assistant recherche" };

export default function CabinetPanel() {
  const [info, setInfo] = useState(null);
  const [rename, setRename] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState("assistant");
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/cabinet/me");
    setInfo(data);
    setRename(data.cabinet_name || "");
  };
  useEffect(() => { load(); }, []);

  const saveName = async () => {
    if (!rename.trim()) return;
    setBusy(true);
    try {
      await api.put("/cabinet/me", { name: rename.trim() });
      toast.success("Nom du cabinet mis à jour");
      load();
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const invite = async () => {
    if (!invEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/cabinet/invite", { email: invEmail.trim(), role: invRole });
      if (data.status === "already_member") toast.info("Cette personne fait déjà partie du cabinet");
      else toast.success("Invitation envoyée");
      setInvEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    try { await api.delete(`/cabinet/invite/${id}`); toast.success("Invitation annulée"); load(); }
    catch { toast.error("Erreur"); }
  };

  const copyLink = (link, id) => {
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    toast.success("Lien copié");
    setTimeout(() => setCopiedId(null), 1500);
  };

  if (!info) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
          <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-4">Nom du cabinet</h2>
          <div className="flex gap-2">
            <Input value={rename} onChange={(e) => setRename(e.target.value)} className="bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" data-testid="cabinet-name-input" />
            <Button onClick={saveName} disabled={busy} className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" data-testid="cabinet-name-save">Enregistrer</Button>
          </div>
          <p className="text-xs text-[#5F758F] mt-3">Visible pour tous les praticiens du cabinet et dans les invitations envoyées par email.</p>
        </div>

        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
          <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-2">Membres</h2>
          <p className="text-sm text-[#5F758F] mb-4">Ces praticiens partagent {info.shared_templates} modèle{info.shared_templates > 1 ? "s" : ""} de questionnaire. <strong>Chaque dossier patient reste privé au praticien qui l'a créé.</strong></p>
          <ul className="divide-y divide-[#E1E9F0] -mx-6">
            {info.members.map((m) => (
              <li key={m.user_id} className="px-6 py-3 flex items-center justify-between" data-testid={`member-${m.user_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[10px] bg-[#3A8FA5] text-white flex items-center justify-center font-bold text-sm">
                    {`${(m.name || "?").split(" ").map((w) => w.charAt(0)).slice(0, 2).join("")}`.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="text-[#1E3A5F] font-medium">{m.name}</p>
                    <p className="text-xs text-[#5F758F]">{m.email} · {m.specialty || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={(m.role || "admin") === "admin"
                    ? "bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6] hover:bg-[#E6F1F5]"
                    : "bg-[#E1E9F0] text-[#1E3A5F] hover:bg-[#E1E9F0]"}>
                    {ROLE_LABEL[m.role || "admin"]}
                  </Badge>
                  {m.is_owner && <span className="text-xs tracking-widest uppercase text-[#3A8FA5]">Titulaire</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {info.invites.length > 0 && (
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
            <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-4">Invitations en attente</h2>
            <ul className="divide-y divide-[#E1E9F0] -mx-6">
              {info.invites.map((iv) => {
                const link = `${window.location.origin}/cabinet/join?token=${iv.token}`;
                return (
                  <li key={iv.invite_id} className="px-6 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[#1E3A5F] truncate">{iv.email}</p>
                      <p className="text-xs text-[#5F758F]">Invité le {new Date(iv.created_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => copyLink(link, iv.invite_id)}>
                        {copiedId === iv.invite_id ? <Check size={14} /> : <Copy size={14} />}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => cancel(iv.invite_id)}><Trash size={14} /></Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <aside className="lg:col-span-1">
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6 sticky top-24">
          <Users size={28} weight="duotone" className="text-[#3A8FA5] mb-3" />
          <h3 className="text-xl font-bold tracking-tight text-[#1E3A5F] mb-3">Ajouter un collaborateur</h3>
          <p className="text-sm text-[#5F758F] mb-4">Il recevra un email et rejoindra le cabinet après avoir cliqué sur le lien avec ce même email.</p>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Email</Label>
          <Input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="collegue@exemple.fr" className="mt-2 mb-3 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" data-testid="invite-email" />
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Rôle</Label>
          <Select value={invRole} onValueChange={setInvRole}>
            <SelectTrigger className="mt-2 mb-1 bg-white border-[#E1E9F0]" data-testid="invite-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Praticien — accès complet</SelectItem>
              <SelectItem value="assistant">Assistant — gère les patients et envoie les questionnaires</SelectItem>
              <SelectItem value="research">Assistant de recherche clinique — accès Études uniquement</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-[#5F758F] mb-3">
            {invRole === "admin"
              ? "Peut créer et modifier des questionnaires, inviter d'autres membres et voir le journal d'audit."
              : invRole === "research"
              ? "Accès restreint au module Études : statistiques anonymisées, comparaison de cohortes, analyse IA de compte-rendus. Ne voit ni patients ni réponses individuelles."
              : "Ne peut pas créer ni modifier de questionnaires ; il envoie ceux du cabinet aux patients."}
          </p>
          <Button onClick={invite} disabled={busy || !invEmail} className="w-full rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" data-testid="invite-submit">
            <PaperPlaneTilt size={16} className="mr-2" /> Envoyer l'invitation
          </Button>
        </div>
      </aside>
    </div>
  );
}
